import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaMapMarkerAlt, FaCar, FaWalking, FaSubway } from "react-icons/fa";
import React from "react";

const nearbyLocations = [
  {
    name: "Downtown Dubai",
    distance: "28 mins",
    icon: <FaMapMarkerAlt className="text-2xl text-gold-500" />,
    transport: <FaCar className="text-gray-300" />,
    description: "Dubai's vibrant business and entertainment hub",
  },
  {
    name: "Dubai Mall",
    distance: "28 mins",
    icon: <FaMapMarkerAlt className="text-2xl text-gold-500" />,
    transport: <FaSubway className="text-gray-300" />,
    description: "World's largest shopping and entertainment destination",
  },
  {
    name: "Burj Khalifa",
    distance: "28 mins",
    icon: <FaMapMarkerAlt className="text-2xl text-gold-500" />,
    transport: <FaWalking className="text-gray-300" />,
    description: "Tallest building in the world with observation decks",
  },
  {
    name: "Dubai International Airport",
    distance: "35 mins",
    icon: <FaMapMarkerAlt className="text-2xl text-gold-500" />,
    transport: <FaCar className="text-gray-300" />,
    description: "World's busiest airport for international passengers",
  },
];

const LocationMap = () => {
  const [activeLocation, setActiveLocation] = useState<number | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMapLoaded(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="location"
      aria-labelledby="location-heading"
      className="relative py-24 bg-gradient-to-b from-[#f8f9fa] to-[#e9ecef]"
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.05),transparent_50%)]"></div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2
            id="location-heading"
            className="text-5xl md:text-6xl font-bold tracking-tight mb-6"
          >
            <span className=" text-gold-500">
              Prime Location
            </span>
            
          </h2>
          <p className="text-xl text-midnightBlack max-w-3xl mx-auto mt-4 leading-relaxed">
            Strategically located with excellent connectivity to Dubai's most
            prestigious destinations
          </p>
          <div className="w-20 h-1 mt-8 bg-gradient-to-r from-gold-400 to-gold-600 mx-auto"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Interactive Map Section */}
          <motion.div
            className="relative h-[600px] lg:h-[700px] rounded-3xl overflow-hidden border border-amber-500/20 shadow-[0_0_45px_-12px_rgba(251,191,36,0.3)] backdrop-blur-sm"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            // transition={{ duration: 0.8 }}
          >
            {/* Map Loading Skeleton */}
            {!mapLoaded && (
              <div className="absolute inset-0 bg-gray-900/90 animate-pulse flex items-center justify-center z-20">
                <div className="text-gold-500 text-xl font-medium">
                  Loading map...
                </div>
              </div>
            )}

            {/* Map Overlay Elements */}
            <div className="absolute inset-0 pointer-events-none z-10">
              <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 to-transparent"></div>

              {/* Location Markers */}
              {/* {nearbyLocations.map((_, index) => (
                <motion.div
                  key={index}
                  className={`absolute w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                    activeLocation === index ? "scale-125" : ""
                  }`}
                  style={{
                    top: `${20 + index * 15}%`,
                    left: `${40 + index * 5}%`,
                  }}
                  animate={{
                    backgroundColor:
                      activeLocation === index
                        ? "rgba(251, 191, 36, 0.9)"
                        : "rgba(255, 255, 255, 0.9)",
                    boxShadow:
                      activeLocation === index
                        ? "0 0 20px rgba(251, 191, 36, 0.5)"
                        : "0 0 10px rgba(255, 255, 255, 0.2)",
                  }}
                  whileHover={{ scale: 1.2 }}
                >
                  <span
                    className={`text-lg font-semibold ${
                      activeLocation === index ? "text-white" : "text-amber-600"
                    }`}
                  >
                    {index + 1}
                  </span>
                </motion.div>
              ))} */}
            </div>

            {/* Google Map Iframe */}
            <div className="absolute inset-0 w-full h-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3615.5508052045466!2d55.454494399999994!3d25.0153751!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f7d5b6e348abf%3A0x200f43e8226e0a1c!2sThe%20Valley%20by%20Emaar!5e0!3m2!1sen!2sin!4v1749637144224!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={() => setMapLoaded(true)}
                className={`transition-opacity duration-500 ${
                  mapLoaded ? "opacity-100" : "opacity-0"
                }`}
              ></iframe>
            </div>
          </motion.div>

          {/* Location Details Section */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-br from-amber-900/10 via-gray-900/50 to-gray-900/30 backdrop-blur-xl rounded-3xl p-8 border border-amber-500/20 shadow-[0_0_45px_-12px_rgba(251,191,36,0.2)]">
              <h3 className="text-3xl font-bold mb-6  text-gold-500">
                Luxury Living, Prime Location
              </h3>
              <p className="text-gray-200 text-lg leading-relaxed mb-8">
                Experience the perfect blend of tranquility and accessibility.
                Rivera offers an exclusive retreat while keeping you connected
                to Dubai's most iconic landmarks and attractions.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-amber-900/90 to-amber-900/40 p-6 rounded-2xl border border-amber-500/30">
                  <div className="text-gold-500 text-lg mb-2">By Car</div>
                  <div className="text-white text-2xl font-semibold">
                    25-35 mins
                  </div>
                </div>
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/30 p-6 rounded-2xl border border-white/10">
                  <div className="text-gray-300 text-lg mb-2">Metro</div>
                  <div className="text-white text-2xl font-semibold">
                    40 mins
                  </div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {nearbyLocations.map((location, index) => (
                <motion.button
                  key={index}
                  className={`group relative overflow-hidden rounded-2xl p-6 text-left transition-all duration-300 ${
                    activeLocation === index
                      ? "bg-gradient-to-br from-amber-900/40 to-amber-900/20 border-amber-500/40"
                      : "bg-gradient-to-br from-gray-800/30 to-gray-900/30 border-white/10 hover:border-amber-500/30"
                  } border backdrop-blur-xl`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setActiveLocation(index)}
                  aria-label={`View details for ${location.name}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/5 to-amber-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                  <div className="flex items-center gap-4 mb-4">
                    {location.icon}
                    <span className="text-white">{location.transport}</span>
                  </div>
                  <h4 className="text-xl font-bold mb-2 text-white">
                    {location.name}
                  </h4>
                  <p className="text-gold-600 font-semibold mb-3 text-lg">
                    {location.distance}
                  </p>
                  <p className="text-gray-200">{location.description}</p>
                </motion.button>
              ))}
            </div>

            <motion.a
              href="https://maps.app.goo.gl/ei7v9Xr6ETnBdLZH6"
              target="_blank"
              rel="noopener noreferrer"
              className="relative overflow-hidden group flex items-center justify-center w-full text-white bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 font-bold text-lg py-3 px-6 rounded-2xl shadow-lg transition-all duration-300 border border-amber-500/50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-label="Get directions to Rivera on Google Maps"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span className="mr-3">Get Directions</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LocationMap;
